/**
 * Iter 46 — Continuous-scroll PDF viewer for professional proofing.
 *
 * Renders every page of a PDF stacked vertically inside a scroll container —
 * the same feel as Adobe Reader's continuous mode. Zoom in/out preserves the
 * scroll position, text is selectable (renderTextLayer), and the toolbar
 * shows the current page + total via IntersectionObserver on each page.
 *
 * PDF.js worker is served from the same version of pdfjs-dist we bundled
 * (jsdelivr), so we never hit the "API version does not match Worker" trap.
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Loader2, AlertCircle } from "lucide-react";

// Iter 60 — Serve the PDF.js worker from the same origin as the app to fix
// Safari/iPad blank-page issues. When workerSrc is cross-origin, iOS Safari
// occasionally rejects the .mjs MIME type or blocks under Cross-Origin-Isolated
// policy, leaving the viewer stuck. `public/pdf.worker.min.mjs` is copied at
// build time from `node_modules/pdfjs-dist/build/` so the version always
// matches the bundled pdfjs (pinned in package.json).
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0];

// Iter 47 — expose an imperative `scrollToPage(n)` handle so parent components
// (comment list) can deep-link into a specific page from a "Page N" pill. Also
// emit `onPageChange(n)` upstream whenever the active page shifts so the
// commenter box can capture the current page as an anchor.
const PdfViewer = forwardRef(function PdfViewer({ url, title, initialPage = 1, onPageChange, renderPageOverlay }, ref) {
  const [numPages, setNumPages] = useState(0);
  const [activePage, setActivePage] = useState(initialPage);
  const [zoomIdx, setZoomIdx] = useState(2); // 1.0 by default
  const [fitWidth, setFitWidth] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef(null);
  const pageRefs = useRef([]);
  // Iter 48 — Navigation hardening.
  //   `targetPageRef` is the user's *intent* (last requested page), not
  //   whatever IntersectionObserver thinks is most visible right now.
  //   `isJumpingRef` locks IntersectionObserver out for the duration of a
  //   programmatic smooth-scroll so a single Next click can't be shadowed by
  //   an IO callback that says "you're still on page 1".
  const targetPageRef = useRef(initialPage);
  const isJumpingRef = useRef(false);
  const jumpTimerRef = useRef(null);

  // react-pdf's `file` prop must be a stable reference — otherwise it re-fetches
  // on every render. Memoize with { url, withCredentials } shape.
  const fileProp = useMemo(() => ({ url }), [url]);

  // Track container width for fit-to-width rendering. ResizeObserver keeps
  // the pages sharp when the modal is resized.
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const ro = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // IntersectionObserver on each rendered page — whichever page is most
  // visible becomes the active page counter shown in the toolbar.
  useEffect(() => {
    if (!numPages || !scrollRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Iter 48 — Ignore IO updates while a programmatic jump is in flight.
        // Otherwise mid-animation intersections cause the indicator to flicker
        // through intermediate pages and steal the Prev/Next arrow's next click.
        if (isJumpingRef.current) return;
        let best = null;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
        if (best) {
          const p = parseInt(best.target.dataset.page, 10);
          if (p) {
            setActivePage(p);
            targetPageRef.current = p;
          }
        }
      },
      { root: scrollRef.current, threshold: [0.25, 0.5, 0.75] }
    );
    pageRefs.current.forEach((n) => n && obs.observe(n));
    return () => obs.disconnect();
  }, [numPages]);

  // Iter 48 — Strict snap-to-top jump. `offsetTop` (no fudge factor) so the
  // page's top edge locks to the scroll container's top-left corner.
  //
  // Layout-shift correction: react-pdf renders each page lazily, so a page
  // above the target may resize (skeleton height ≠ real rendered height)
  // seconds AFTER the initial jump, silently pushing the target's offsetTop
  // downward. To guarantee exact snap-to-top we run a two-layer correction:
  //   1. Immediate smooth scroll toward the computed offsetTop.
  //   2. A ResizeObserver on every page instantly re-aligns scrollTop to the
  //      target's fresh offsetTop whenever ANY page resizes while a jump is
  //      active. This catches lazy renders that fire long after the initial
  //      scroll animation would have ended.
  //   3. A stability watchdog ends the jump 800ms after the last resize (or
  //      at a 5s hard cap) so IntersectionObserver takes over cleanly.
  // The IO lockout stays engaged the whole time so the toolbar indicator
  // never flickers through intermediate pages.
  const jumpTo = (p) => {
    if (!numPages) return;
    const clamped = Math.max(1, Math.min(numPages, p));
    const node = pageRefs.current[clamped - 1];
    const container = scrollRef.current;
    if (!node || !container) return;
    targetPageRef.current = clamped;
    setActivePage(clamped); // optimistic — toolbar updates immediately
    isJumpingRef.current = true;

    // Iter 49 — Bounding-rect delta math (works across Admin modal AND client
    // share route). offsetTop is relative to offsetParent, which can differ
    // between contexts (positioned ancestors, transforms, padding, etc). The
    // rect delta measures the PIXEL distance the page needs to travel to sit
    // flush with the container's inner top, independent of layout parentage.
    const computeTarget = () => {
      const nodeRect = node.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const delta = nodeRect.top - containerRect.top;
      const paddingTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
      return container.scrollTop + delta - paddingTop;
    };
    container.scrollTo({ top: computeTarget(), behavior: "smooth" });

    // Tear down any prior jump's observers/timers before starting a new one.
    if (jumpTimerRef.current) {
      if (jumpTimerRef.current.stabilityTimer) clearTimeout(jumpTimerRef.current.stabilityTimer);
      if (jumpTimerRef.current.hardCap) clearTimeout(jumpTimerRef.current.hardCap);
      if (jumpTimerRef.current.ro) jumpTimerRef.current.ro.disconnect();
      if (jumpTimerRef.current.armTimer) clearTimeout(jumpTimerRef.current.armTimer);
      jumpTimerRef.current = null;
    }

    // Iter 49c — Two-phase timing so the smooth ease-in plays cleanly:
    //   Phase A (0-600ms): smooth-scroll animation runs untouched. No RO
    //                      corrections, no realigns. Users see the ease-in.
    //   Phase B (>600ms):  precision lock engages. Any resize (lazy page
    //                      renders) instantly re-snaps via direct scrollTop
    //                      assignment (invisible micro-correction).
    const alignArmedRef = { current: false };

    // Realign on every layout shift while the jump is active. Uses the same
    // rect-delta math so it works identically in Admin modal and Share view.
    // Direct scrollTop assignment is instant and doesn't animate, so users
    // only see the initial smooth scroll — subsequent corrections are invisible.
    const realign = () => {
      const currentNode = pageRefs.current[clamped - 1];
      const c = scrollRef.current;
      if (!currentNode || !c) return;
      const nodeRect = currentNode.getBoundingClientRect();
      const containerRect = c.getBoundingClientRect();
      const delta = nodeRect.top - containerRect.top;
      const paddingTop = parseFloat(getComputedStyle(c).paddingTop) || 0;
      const target = Math.max(0, c.scrollTop + delta - paddingTop);
      if (Math.abs(c.scrollTop - target) > 0.5) {
        c.scrollTop = target;
      }
    };

    const ro = new ResizeObserver(() => {
      if (!isJumpingRef.current) return;
      if (!alignArmedRef.current) return; // Phase A — let the smooth scroll play
      realign();
      // Push the stability deadline forward on every resize.
      if (jumpTimerRef.current?.stabilityTimer) {
        clearTimeout(jumpTimerRef.current.stabilityTimer);
      }
      if (jumpTimerRef.current) {
        jumpTimerRef.current.stabilityTimer = setTimeout(endJump, 800);
      }
    });
    pageRefs.current.forEach((n) => n && ro.observe(n));

    // Arm precision locking after the smooth-scroll window (~600ms). Before
    // arming, do ONE realign to catch any lazy-render shifts that happened
    // during the animation — this is invisible because it runs at the same
    // moment the smooth animation is settling.
    const armTimer = setTimeout(() => {
      alignArmedRef.current = true;
      realign();
    }, 600);

    const endJump = () => {
      if (alignArmedRef.current) realign(); // final lock; skip if smooth-scroll still landing
      ro.disconnect();
      if (jumpTimerRef.current?.hardCap) clearTimeout(jumpTimerRef.current.hardCap);
      if (jumpTimerRef.current?.stabilityTimer) clearTimeout(jumpTimerRef.current.stabilityTimer);
      if (jumpTimerRef.current?.armTimer) clearTimeout(jumpTimerRef.current.armTimer);
      jumpTimerRef.current = null;
      isJumpingRef.current = false;
    };

    jumpTimerRef.current = {
      // Stability watchdog is only meaningful once we're in Phase B, so we
      // set it AFTER the arm delay. Until then the hard cap is our safety net.
      stabilityTimer: setTimeout(endJump, 600 + 800),
      hardCap: setTimeout(endJump, 5000), // absolute ceiling
      armTimer,
      ro,
    };
  };

  // Prev/Next drive off targetPageRef (user intent) so a click always moves
  // exactly one page — never shadowed by a mid-scroll IO update.
  const stepPage = (delta) => {
    const from = targetPageRef.current || activePage || 1;
    jumpTo(from + delta);
  };

  // Emit page changes upstream so the commenter box always knows the current page.
  useEffect(() => {
    if (onPageChange && activePage) onPageChange(activePage);
  }, [activePage, onPageChange]);

  useImperativeHandle(ref, () => ({
    scrollToPage: (p) => {
      if (!p || p < 1) return;
      jumpTo(p);
    },
    getActivePage: () => activePage,
    getPageCount: () => numPages,
  }), [activePage, numPages]);

  const zoomIn = () => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1));
  const zoomOut = () => setZoomIdx((i) => Math.max(0, i - 1));
  const currentZoom = ZOOM_STEPS[zoomIdx];

  // When Fit-Width is on, the page width equals the container width and zoom
  // acts as a multiplier on top of that. When off, we render at 800px baseline.
  const pageWidth = fitWidth
    ? Math.max(320, (containerWidth || 800) - 24) * currentZoom
    : 800 * currentZoom;

  return (
    <div className="w-full h-full flex flex-col bg-black min-h-[400px]" data-testid="pdf-viewer">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-950/80 text-xs">
        <div className="flex items-center gap-2 text-zinc-300">
          <button
            type="button"
            data-testid="pdf-prev-page"
            onClick={() => stepPage(-1)}
            disabled={activePage <= 1}
            className="p-1 rounded hover:bg-zinc-800 disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="min-w-[70px] text-center font-mono" data-testid="pdf-page-indicator">
            {numPages ? `${activePage} / ${numPages}` : "…"}
          </div>
          <button
            type="button"
            data-testid="pdf-next-page"
            onClick={() => stepPage(1)}
            disabled={activePage >= numPages}
            className="p-1 rounded hover:bg-zinc-800 disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 text-zinc-300">
          <button
            type="button"
            data-testid="pdf-zoom-out"
            onClick={zoomOut}
            disabled={zoomIdx <= 0}
            className="p-1 rounded hover:bg-zinc-800 disabled:opacity-40"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <div className="min-w-[52px] text-center font-mono">{Math.round(currentZoom * 100)}%</div>
          <button
            type="button"
            data-testid="pdf-zoom-in"
            onClick={zoomIn}
            disabled={zoomIdx >= ZOOM_STEPS.length - 1}
            className="p-1 rounded hover:bg-zinc-800 disabled:opacity-40"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid="pdf-fit-width"
            onClick={() => setFitWidth((v) => !v)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded transition text-[10px] uppercase tracking-wider ${fitWidth ? "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] border border-[var(--brand-accent)]/30" : "border border-zinc-800 hover:border-zinc-700"}`}
            aria-label="Toggle fit-to-width"
          >
            <Maximize2 className="w-3 h-3" /> Fit
          </button>
        </div>
      </div>

      {/* Scroll container */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-auto p-3 bg-zinc-950" data-testid="pdf-scroll-container">
        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-red-300 gap-2">
            <AlertCircle className="w-6 h-6" />
            <div className="text-xs">Could not load PDF: {error}</div>
          </div>
        ) : (
          <Document
            file={fileProp}
            onLoadSuccess={({ numPages }) => { setNumPages(numPages); setError(null); }}
            onLoadError={(e) => setError(e?.message || "Unknown error")}
            loading={
              <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
                <div className="text-[10px] uppercase tracking-[0.2em]">Loading document</div>
              </div>
            }
            className="flex flex-col items-center gap-3"
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div
                key={i + 1}
                ref={(el) => (pageRefs.current[i] = el)}
                data-page={i + 1}
                data-testid={`pdf-page-${i + 1}`}
                className="shadow-2xl shadow-black/60 bg-white relative"
              >
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  loading={
                    <div style={{ width: pageWidth, height: pageWidth * 1.294 }} className="flex items-center justify-center bg-zinc-900">
                      <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
                    </div>
                  }
                />
                {/* Iter 52 — Optional annotation overlay slot. Parents pass a
                    render-prop that gets (pageNumber, pageWidth) and returns
                    whatever they want to overlay on that page (typically a
                    PdfAnnotationLayer). Absolutely positioned above the Page
                    canvas via `relative` on the wrapper. */}
                {renderPageOverlay && renderPageOverlay(i + 1, pageWidth)}
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
});

export default PdfViewer;
